import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, RefreshCw, Unlink } from "lucide-react";
import { useState } from "react";

import { api, background } from "../../api/client";
import type { DeclinationState, Exercise } from "../../api/types";
import { Banner } from "../../components/Callout";
import { Spinner } from "../../components/EmptyState";
import { Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { receivedNotice } from "./declination";

/** Dossier d'un subordonné ouvert seul (audit du 10/10/2026) : son ordre vient d'une déclinaison et l'ordre de son
 *  chef a changé depuis — mission, LIMA, fuseau peuvent ne plus lui correspondre. Le bandeau le dit et désigne l'action
 *  attendue : « Mettre à jour les ordres subordonnés », relancée depuis le dossier du chef (mêmes dossiers, dans la
 *  langue de la déclinaison), dont la vue des ordres subordonnés s'ouvre pour suivre l'avancement ; dossier du chef
 *  qu'on ne peut pas modifier : il s'ouvre ; non partagé : le bandeau le dit, sans action. Depuis la cascade des ordres
 *  (13/10/2026), le dossier suit de lui-même l'ordre de son chef à son ouverture : ce bandeau ne paraît plus que s'il
 *  n'a pas pu le suivre ; ce qui a été répercuté se lit dans le bandeau des paragraphes mis à jour de l'ordre. Unité
 *  retirée de l'ordre du chef (audit du 15/10/2026) : le bandeau le dit et propose de détacher le dossier (action
 *  attendue) ou de le garder tel quel. */
export function HigherOrderBanner({ exercise }: { exercise: Exercise | undefined }) {
  const queryClient = useQueryClient();
  const setExercise = useUi((s) => s.setExercise);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const [starting, setStarting] = useState(false);
  const id = exercise?.id ?? null;
  // Seul un dossier issu d'une déclinaison porte le repère (`extra.declination`, rangé par le serveur).
  const declined = Boolean(exercise?.parameters.extra?.declination);
  const query = useQuery({
    queryKey: ["declination", id],
    queryFn: () => background(() => api.declination(id as string)),
    enabled: Boolean(id && declined),
    staleTime: 0,
  });
  const received = declined ? query.data?.received : null;
  const notice = receivedNotice(received);

  /** Ouvre la vue des ordres subordonnés du dossier du chef ; l'avis ramène à l'ordre quitté. */
  const openHigher = (fromId: string, fromName: string) => {
    const exercises = queryClient.getQueryData<Exercise[]>(["exercises"]) ?? [];
    const back = exercise;
    setExercise(fromId, exercises.find((e) => e.id === fromId)?.map_slug ?? null);
    setView("declination");
    notify({
      text: t("Dossier ouvert : {name}", { name: fromName }),
      tone: "info",
      action: back
        ? {
            label: t("Revenir à l'ordre du subordonné"),
            run: () => {
              setExercise(back.id, back.map_slug);
              setView("order");
            },
          }
        : undefined,
    });
  };

  const update = async () => {
    const from = received?.from;
    if (!from || !received) return;
    setStarting(true);
    try {
      const state = await api.startDeclination(from.id, received.language, received.opo);
      queryClient.setQueryData<DeclinationState>(["declination", from.id], state);
      if (id) void queryClient.invalidateQueries({ queryKey: ["declination", id] });
      openHigher(from.id, from.name);
    } catch (e) {
      notify({ text: t("Mise à jour impossible"), detail: (e as Error).message, tone: "error" });
    } finally {
      setStarting(false);
    }
  };

  /** Dossier retiré de l'ordre du chef (audit du 15/10/2026, axe 3 n° 1) : le détacher ou le garder tel quel. */
  const [settling, setSettling] = useState<"detach" | "keep" | null>(null);
  const settle = async (action: "detach" | "keep") => {
    if (!id) return;
    setSettling(action);
    try {
      await api.settleRemoved(id, action);
      void queryClient.invalidateQueries({ queryKey: ["declination"] });
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      notify({
        text: action === "detach" ? t("Dossier détaché : il ne reçoit plus rien de l'ordre du chef.") : t("Dossier gardé tel quel."),
        tone: "ok",
      });
    } catch (e) {
      notify({ text: t("Action impossible"), detail: (e as Error).message, tone: "error" });
    } finally {
      setSettling(null);
    }
  };

  const show = Boolean(notice);
  return (
    <Presence show={show}>
      {notice && (
        <Banner
          tone="warn"
          actions={
            notice.action === "removed" ? (
              <>
                <Button size="sm" highlight={!settling} disabled={Boolean(settling)} onClick={() => void settle("detach")}>
                  {settling === "detach" ? <Spinner decorative /> : <Unlink size={14} />} {t("Détacher le dossier")}
                </Button>
                <Button size="sm" variant="secondary" disabled={Boolean(settling)} onClick={() => void settle("keep")}>
                  {settling === "keep" ? <Spinner decorative /> : <Check size={14} />} {t("Le garder tel quel")}
                </Button>
              </>
            ) : notice.action === "update" ? (
              <Button size="sm" highlight={!starting} disabled={starting} onClick={() => void update()}>
                {starting ? <Spinner decorative /> : <RefreshCw size={14} />} {t("Mettre à jour les ordres subordonnés")}
              </Button>
            ) : notice.action === "open" && received?.from ? (
              <Button size="sm" variant="secondary" onClick={() => openHigher(received.from!.id, received.from!.name)}>
                {t("Ouvrir le dossier de l'échelon supérieur")} <ArrowRight size={14} />
              </Button>
            ) : undefined
          }
        >
          {notice.text}
        </Banner>
      )}
    </Presence>
  );
}
